/**
 * requiredGate — WEB-895: тумблер «Обязательное» реально работает на публичном
 * рантайме. Блоки текущего экрана с required=true и пустым значением блокируют
 * продвижение (Next/оплату), у блока показывается warning.
 *
 * «Заполнено» = в FormValues по block.id лежит непустая строка; для
 * чекбокс-семейства (terms_checkbox, toggle, checkbox) — строго 'true'
 * (галочка стоит). ВАЖНО: каждый вводной блок обязан писать своё значение в
 * FormValues по СВОЕМУ block.id — гейт читает только оттуда. Блок, который
 * ничего не пишет (НЕуправляемый input), запирает человека навсегда: значение
 * на экране есть, а гейт видит пустоту. Так ломались phone_input и
 * terms_checkbox (возврат QA 2026-08-01).
 * Скрытые логикой блоки (hiddenSet) не требуем — их не видно и не заполнить.
 *
 * Чистая функция — общая для QuizScreenWizard и PaywallScreenWizard.
 */
import type { EditorBlock } from '@/store/editorSlice';
import { isValidEmail, normalizeEmail } from './emailRule';
import { playerKitDoor } from '@/player/playerKitDoor';
import type { AuthorBounds } from './numberBounds';

/** Блоки, у которых required по умолчанию ВКЛЮЧЁН (паттерн WEB-890). */
const REQUIRED_BY_DEFAULT = new Set(['terms_checkbox']);

/**
 * Чекбокс-семейство: значение 'false' = не заполнено.
 * WEB-895 (возврат QA): 'checkbox' сюда не входил — снятая галочка писала
 * строку 'false', та проходила как «непустая строка», и обязательный чекбокс
 * пропускал вперёд неотмеченным.
 */
const CHECKBOX_TYPES = new Set([
  'terms_checkbox',
  'toggle',
  'checkbox',
  // WEB-2158: явное согласие — та же семья «значение 'false' = не заполнено».
  // В REQUIRED_BY_DEFAULT оно НЕ входит осознанно: по макету 18.09 без галочки
  // посетителя пускают дальше, ответ просто не сохраняется (замок стоит в
  // сборе ответов, `collectScreenAnswers`, а не на кнопке «Далее»).
  'sensitive_consent',
]);

export interface UnfilledRequiredBlock {
  blockId: string;
  blockType: EditorBlock['type'];
}

/**
 * WEB-1716: почему экран не пускает дальше.
 * `required_empty` — обязательное поле пустое (прежний гейт WEB-895).
 * `invalid_email` — в email-поле введено НЕ похожее на почту (`ivan@mail`).
 */
export type ScreenBlockerReason =
  'required_empty' | 'invalid_email' | 'out_of_range';

export type { AuthorBounds };

export interface ScreenBlocker extends UnfilledRequiredBlock {
  reason: ScreenBlockerReason;
  /** Только у `out_of_range`: какие границы нарушены — для подписи отказа. */
  bounds?: AuthorBounds;
}

function isRequired(block: EditorBlock): boolean {
  const b = block as EditorBlock & { required?: boolean };
  if (REQUIRED_BY_DEFAULT.has(block.type)) return b.required ?? true;
  return b.required === true;
}

function isFilled(block: EditorBlock, value: string | undefined): boolean {
  // WEB-1891: переключатель рисуется включённым по `defaultToggled`
  // (ToggleRenderer, defaultChecked) и ничего не пишет, пока его не тронули —
  // нетронутый включённый обязателен-и-заполнен, как видит посетитель.
  if (block.type === 'toggle' && value === undefined) {
    return block.defaultToggled === true;
  }
  if (CHECKBOX_TYPES.has(block.type)) return value === 'true';
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * WEB-1716: найти всё, что держит экран на месте, — с ПРИЧИНОЙ, чтобы рантайм
 * показал покупателю понятную подпись («проверьте адрес», а не «поле
 * обязательное»).
 *
 * Три причины:
 *  - `required_empty` — обязательный блок без значения (гейт WEB-895);
 *  - `out_of_range` — число вне границ, заданных автором (WEB-2351);
 *  - `invalid_email` — в email-блоке лежит непустое значение, не похожее на
 *    почту по ОБЩЕМУ правилу (emailRule.isValidEmail — то же, по которому
 *    создаётся лид). Проверяется независимо от тумблера «Обязательное»:
 *    человек, который что-то ввёл в поле почты, свою почту оставить ХОТЕЛ, а
 *    пропустить его дальше = потерять контакт молча (весь смысл карточки).
 *    Пустое НЕобязательное поле причиной не считается — его не заполняли.
 *
 * @param blocks - блоки текущего экрана.
 * @param values - FormValues публичного рантайма (block.id → значение).
 * @param hiddenSet - скрытые логикой блоки (не требуем и не проверяем).
 */
export function findScreenBlockers(
  blocks: EditorBlock[],
  values: Record<string, string>,
  hiddenSet?: ReadonlySet<string>,
): ScreenBlocker[] {
  const found: ScreenBlocker[] = [];
  for (const block of blocks) {
    if (hiddenSet?.has(block.id)) continue;
    const raw = values[block.id];
    if (isRequired(block) && !isFilled(block, raw)) {
      found.push({
        blockId: block.id,
        blockType: block.type,
        reason: 'required_empty',
      });
      continue;
    }
    // WEB-2351: проверка границ — в ленивом куске плеера (вес первого
    // экрана); куска нет — границы не судим (fail-open, как {{bmi}}).
    const bounds =
      playerKitDoor.get()?.numberBounds.outOfRange(block, values) ?? null;
    if (bounds) {
      found.push({
        blockId: block.id,
        blockType: block.type,
        reason: 'out_of_range',
        bounds,
      });
      continue;
    }
    if (block.type === 'email' && normalizeEmail(raw).length > 0) {
      if (!isValidEmail(raw)) {
        found.push({
          blockId: block.id,
          blockType: block.type,
          reason: 'invalid_email',
        });
      }
    }
  }
  return found;
}

/**
 * Найти на экране обязательные незаполненные блоки.
 *
 * WEB-1716: остаётся прежним контрактом (без причин и без проверки почты) —
 * на него смотрит PaywallScreenWizard, чей путь лида живёт в своём модуле
 * (paywall-runtime) и этой карточкой не трогается. Рантайм квиза перешёл на
 * findScreenBlockers.
 *
 * @param blocks - блоки текущего экрана.
 * @param values - FormValues публичного рантайма (block.id → значение).
 * @param hiddenSet - скрытые логикой блоки (не требуем).
 */
export function findUnfilledRequired(
  blocks: EditorBlock[],
  values: Record<string, string>,
  hiddenSet?: ReadonlySet<string>,
): UnfilledRequiredBlock[] {
  const found: UnfilledRequiredBlock[] = [];
  for (const block of blocks) {
    if (hiddenSet?.has(block.id)) continue;
    if (!isRequired(block)) continue;
    if (isFilled(block, values[block.id])) continue;
    found.push({ blockId: block.id, blockType: block.type });
  }
  return found;
}

/** Текст warning'а у незаполненного обязательного блока. */
// WEB-967: подпись живёт в словаре getPublicQuizStrings(language)
// (requiredFieldWarning) — русский хардкод показывался на всех языках.
